import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { SERVICES } from "@/components/landing/sections";

const inputClass = "w-full rounded-2xl border border-[var(--border)] bg-[var(--bg)] px-4 py-3 text-sm outline-none transition-colors focus:border-[var(--mint-dark)] focus:bg-white";
const FIELDS = ["nombre", "telefono", "correo", "servicio", "fecha_preferida", "horario_preferido", "comentarios"];
const pick = (a) => Object.fromEntries(FIELDS.map((k) => [k, a[k] ?? ""]));

function Field({ label, children, span }) {
  return (
    <label className={`block ${span ? "sm:col-span-2" : ""}`}>
      <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>{label}</span>
      {children}
    </label>
  );
}

export default function EditAppointmentDialog({ a, onSaved, onUnauthorized }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(() => pick(a));
  const [slots, setSlots] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setForm(pick(a)); }, [open, a]);
  useEffect(() => {
    if (open) api.get("/availability").then(({ data }) => setSlots(data.slots || [])).catch(() => {});
  }, [open]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const t = (k) => `edit-${k.split("_")[0]}-${a.id}`;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/appointments/${a.id}`, form);
      toast.success("Cita actualizada");
      setOpen(false);
      onSaved();
    } catch (err) {
      if (err.response?.status === 401) { onUnauthorized(); return; }
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Revisa los datos: hay campos inválidos.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={`edit-appointment-${a.id}`} aria-label="Editar cita"
          className="inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-xs font-semibold transition-colors hover:bg-[var(--bg)]"
          style={{ border: "1px solid var(--border)", color: "var(--text-dark)" }}>
          <Pencil size={13} /> Editar
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-lg rounded-[1.5rem]" data-testid={`edit-dialog-${a.id}`}>
        <DialogHeader>
          <DialogTitle className="font-display">Editar cita</DialogTitle>
          <DialogDescription>Corrige los datos del paciente o mueve la cita de fecha y horario. Solo puede haber una cita por día.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="grid gap-4 sm:grid-cols-2">
          <Field label="Nombre" span><input data-testid={t("nombre")} className={inputClass} value={form.nombre} onChange={set("nombre")} required /></Field>
          <Field label="Teléfono"><input data-testid={t("telefono")} className={inputClass} value={form.telefono} onChange={set("telefono")} required /></Field>
          <Field label="Correo"><input data-testid={t("correo")} type="email" className={inputClass} value={form.correo} onChange={set("correo")} required /></Field>
          <Field label="Servicio" span>
            <select data-testid={t("servicio")} className={inputClass} value={form.servicio} onChange={set("servicio")}>
              {!SERVICES.some((s) => s.title === form.servicio) && <option value={form.servicio}>{form.servicio}</option>}
              {SERVICES.map((s) => <option key={s.title} value={s.title}>{s.title}</option>)}
            </select>
          </Field>
          <Field label="Fecha"><input data-testid={t("fecha_preferida")} type="date" className={inputClass} value={form.fecha_preferida} onChange={set("fecha_preferida")} required /></Field>
          <Field label="Horario">
            <select data-testid={t("horario_preferido")} className={inputClass} value={form.horario_preferido} onChange={set("horario_preferido")}>
              {(slots.length ? slots : [form.horario_preferido]).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Comentarios" span><textarea data-testid={t("comentarios")} rows={2} className={inputClass + " resize-none"} value={form.comentarios} onChange={set("comentarios")} /></Field>
          <DialogFooter className="sm:col-span-2">
            <button type="button" onClick={() => setOpen(false)} data-testid={`cancel-edit-${a.id}`}
              className="rounded-full border px-5 py-2.5 text-sm font-semibold" style={{ borderColor: "var(--border)", color: "var(--text-dark)" }}>Cancelar</button>
            <button type="submit" disabled={saving} data-testid={`save-edit-${a.id}`}
              className="rounded-full px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60" style={{ background: "var(--mint-dark)" }}>
              {saving ? "Guardando..." : "Guardar cambios"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
